import React from 'react';
import styled from 'styled-components';
import {SIZES, Image, Line, Typography, COLORS} from '@fupa/fupa-uikit';
import {useSelector} from 'react-redux';
import {StyledTypography} from 'app/styles/content';
import {convertToRoman} from 'app/helpers/convertToRoman';
import {COMPETITION_IMAGE_FALLBACK} from 'app/helpers/helpers';
import {SearchFollowButton} from 'app/components/search/SearchFollowButton';
import {selectAuthState} from 'app/hooks/reduxCreateSelectorHooks';
import {useHistory} from 'react-router';
import {updateStoredSearchHistory} from 'app/helpers/searchHelpers';
import {sendEventToGA4} from 'app/helpers/gtagHelpers';

const Content = styled.div`
  display: flex;
  flex: 0 100%;
  flex-direction: column;
`;

const EntityName = styled.div`
  width: 100%;
  display: flex;
  white-space: nowrap;
  overflow: hidden;
`;

const Container = styled.div`
  display: flex;
  flex: 0 100%;
  height: ${SIZES['42']};
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  align-content: center;
  padding: 0.25rem 1rem;
  cursor: pointer;
  &:hover {
    background-color: ${COLORS.lightestGrey};
  }
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: flex-start;
  overflow: hidden;
  margin-left: 0.75rem;
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Placeholder = styled.div`
  width: ${SIZES['32']};
  height: ${SIZES['32']};
`;

const SearchResult = ({result, index}) => {
  const isAuthenticated = useSelector(selectAuthState);
  const history = useHistory();

  const clickHandler = () => {
    sendEventToGA4('select_content', {
      content_type: result.type,
      item_id: result.slug,
      index,
    });
    updateStoredSearchHistory(result);
    history.push(`/${resultEntity.link}/${result.slug}`);
  };

  const getTeamseasonSubtitle = result => {
    const unindicatedCompetition =
      result?.competition?.slug === 'uebergangsliga' || result?.competition?.slug === 'ohne-liga-teams';
    let subtitle = unindicatedCompetition && result?.ageGroup?.name ? `${result.ageGroup.name} | ` : '';
    subtitle += result?.competition?.name ?? 'Unbekannt';
    subtitle += result?.season?.name ? ` ${result.season.name}` : '';

    return subtitle;
  };

  const getResultEntity = result =>
    ({
      player: {
        subtitle: result?.club?.name ?? 'ohne Verein',
        link: 'player',
        entity: 'player',
        name: result.name,
      },
      referee: {
        subtitle: result?.club?.name ?? 'ohne Verein',
        link: 'referee',
        entity: 'player',
        name: result.name,
      },
      competition: {
        subtitle: result?.district?.name ?? '',
        link: result.category === 1 ? 'league' : 'cup',
        entity: 'competition',
        name: result.name,
      },
      club: {
        subtitle: result?.district?.name ?? '',
        link: 'club',
        entity: 'emblem',
        name: result.name,
      },
      news: {
        subtitle: result.authorAlias ?? result?.author?.name ?? '',
        link: 'news',
        entity: 'news',
        name: result.name,
      },
      district: {
        subtitle: result?.region?.name ?? '',
        link: 'region',
        entity: 'district',
        name: result.name,
      },
      teamseason: {
        subtitle: getTeamseasonSubtitle(result),
        link: 'team',
        entity: 'district',
        name: result?.names?.full ?? '',
        add: result?.level > 1 ? `${convertToRoman(result.level)}` : '',
      },
    }[result.type]);

  const resultEntity = getResultEntity(result);

  // Exception for placeholder image of competition
  const competitionImage = result.type === 'competition' && !result.image.path.includes(COMPETITION_IMAGE_FALLBACK);
  const teamseasonImage = result.type === 'teamseason';

  const SearchImage = result.image ? (
    <Image
      circle={!teamseasonImage && !competitionImage}
      entity={resultEntity.entity}
      ratio='1:1'
      width={32}
      height={32}
      fit={teamseasonImage || competitionImage}
      src={result.image}
      eager={true}
    />
  ) : (
    <Placeholder />
  );

  const checkFollowable = entity => {
    if (isAuthenticated) {
      const followableEntities = ['teamseason', 'player', 'competition'];
      const isNotFollowableTeam = entity.type === 'teamseason' && (!entity.teams || entity.teams?.length > 1);
      return followableEntities.includes(entity.type) && !isNotFollowableTeam;
    } else {
      return false;
    }
  };

  const showFollowButton = checkFollowable(result) ? <SearchFollowButton entity={result} /> : null;

  return (
    <Content>
      <Wrapper>
        <Container onClick={clickHandler}>
          {SearchImage}
          <InnerContainer>
            <EntityName>
              <StyledTypography variant='body1' component='div' useOverflow>
                {resultEntity.name}
              </StyledTypography>
              <Typography variant='body1'>&nbsp;{resultEntity.add ?? ''}</Typography>
            </EntityName>
            <StyledTypography variant='caption1' lineHeight={16}>
              {resultEntity.subtitle}
            </StyledTypography>
          </InnerContainer>
        </Container>
        {showFollowButton}
      </Wrapper>
      <Line />
    </Content>
  );
};

export default SearchResult;
